// eslint-disable-next-line no-restricted-imports
import * as engagement from "@amplitude/engagement-browser"

import { ErrorService } from "src/utils/ErrorService"
import { logger } from "src/utils/logger"

declare global {
  interface Window {
    engagement: {
      boot: (options: {
        user: {
          user_id: string
        }
        integrations: {
          track: (event: {
            event_type: string
            event_properties: Record<string, unknown>
          }) => void
        }[]
      }) => void
      forwardEvent: (event: {
        event_type: string
        event_properties: unknown
      }) => void
      setThemeMode: (mode: "lightMode" | "darkMode" | "auto") => void
      setRouter: (router: (url: string) => void) => void
    }
  }
}

type TBootOptions = {
  userId: string
  onEngagementEvent: (event: {
    eventType: string
    eventProperties: Record<string, unknown>
  }) => void
  onRouteChange: (url: string) => void
}

/**
 * Initializes the engagement service with the Amplitude API key.
 * This must be called before any other engagement service functions.
 */
function init() {
  const apiKey = import.meta.env.REACT_APP_AMPLITUDE_API_KEY

  engagement.init(apiKey)
  logger.info("Engagement service initialized")
}

/**
 * Boots up the engagement service for a specific user.
 * This configures event tracking, routing, and theme settings.
 * Must be called after init() and requires the engagement service to be available on window.
 *
 * @param  options.userId - The ID of the user to track
 * @param onEngagementEvent - Callback fired when an event occurs within the engagement
 * @param  onRouteChange - Callback fired when route changes are requested
 * @returns
 */
function boot({ userId, onEngagementEvent, onRouteChange }: TBootOptions) {
  if (!("engagement" in window)) {
    ErrorService.captureException(
      new Error(
        "Engagement service not initialized when boot function was called"
      )
    )
    return
  }

  window.engagement.boot({
    user: {
      user_id: userId,
    },
    integrations: [
      {
        track: (event) => {
          onEngagementEvent({
            eventType: event.event_type,
            eventProperties: event.event_properties,
          })
        },
      },
    ],
  })

  window.engagement.setRouter(onRouteChange)
  window.engagement.setThemeMode("lightMode")
  logger.info("Engagement service booted")
}

/**
 * Forwards an analytics event to the engagement service.
 * Must be called after init() and requires the engagement service to be available on window.
 *
 * @param eventType - The type/name of the event
 * @param eventProperties - Additional properties/data associated with the event
 */
function track(event: { eventType: string; eventProperties: unknown }) {
  if (!("engagement" in window)) {
    ErrorService.captureException(
      new Error(
        "Engagement service not initialized when track function was called"
      )
    )
    return
  }

  window.engagement.forwardEvent({
    event_type: event.eventType,
    event_properties: event.eventProperties,
  })
}

export const EngagementService = {
  init,
  boot,
  track,
}
